/**
 * API contracts shared by the server and both clients. The server validates every input with these
 * schemas; clients use the inferred types. No business logic here.
 */
import { z } from "zod";
import { RegistrationProgram } from "./registrations.js";
import { Gender, password, phone } from "./fields.js";
import { Permission } from "./roles.js";
export const ROLES = [
    "platform_admin",
    "org_admin",
    "branch_manager",
    "supervisor",
    "teacher",
    "student",
];
export const Role = z.enum(ROLES);
/** Header carrying the role the user is acting in. The server verifies the user holds it. */
export const ROLE_HEADER = "x-mubin-role";
export const IDEMPOTENCY_HEADER = "idempotency-key";
export const ErrorCode = z.enum([
    "bad_request",
    "unauthenticated",
    "forbidden",
    "not_found",
    "conflict",
    "idempotency_mismatch",
    "rate_limited",
    "internal",
]);
export const ErrorResponse = z.object({
    error: z.object({ code: ErrorCode, message: z.string(), details: z.unknown().optional() }),
});
// ---------- auth ----------
/** Every account signs in with its phone number (owner decision 2026-10-21, ADR 0019). */
export const LoginRequest = z.object({
    phone,
    password: z.string().min(8).max(128),
});
export const LoginResponse = z.object({
    /** Session token for mobile clients (Authorization: Bearer). Web clients use the httpOnly cookie instead. */
    token: z.string(),
    expiresAt: z.string(),
});
/** A one-time code by WhatsApp: to sign in, or to set a new password. */
/** register: a new student's number, checked before the account opens (owner decision 2026-11-03). */
export const OtpPurpose = z.enum(["login", "reset", "register"]);
export const OTP_LENGTH = 6;
const otpCode = z
    .string()
    .trim()
    .regex(new RegExp(`^[0-9]{${OTP_LENGTH}}$`), `الرمز ${OTP_LENGTH} أرقام`);
export const SendOtpRequest = z.object({ phone, purpose: OtpPurpose.default("login") });
export const SendOtpResponse = z.object({
    /** Seconds until another code may be requested for this number. */
    retryAfter: z.number().int(),
    /** A student's sign-up with a number that has an account: the code adds the student role to it. */
    existingAccount: z.boolean().optional(),
});
export const VerifyOtpRequest = z.object({ phone, code: otpCode });
export const VerifyOtpResponse = LoginResponse.extend({
    /** First sign-in: the account has no password yet and must choose one. */
    needsPassword: z.boolean(),
});
/** The signed-in user's first password (accounts are created without one). */
export const FirstPasswordRequest = z.object({ password });
/** A new password with a reset code sent by WhatsApp; signs the account out everywhere. */
export const ResetPasswordWithOtpRequest = z.object({ phone, code: otpCode, password });
// ---------- me ----------
export const Capability = z.enum([
    "circles.read",
    "circles.manage",
    "students.read",
    "users.manage",
    "roles.grant",
    "audit.read",
    "recitation.request",
    "recitation.conduct",
]);
export const MeResponse = z.object({
    user: z.object({
        id: z.uuid(),
        name: z.string(),
        phone: z.string().nullable(),
        locale: z.string(),
        /** False until the user chooses a password after their first sign-in by code. */
        hasPassword: z.boolean(),
    }),
    roles: z.array(Role),
    activeRole: Role.nullable(),
    /** UI hints only. Every request is authorized again on the server and in the database. */
    capabilities: z.array(Capability),
    /** The active role's permissions (ADR 0018), for showing sections and actions; also checked on the server. */
    permissions: z.array(Permission),
    /** An organization administrator with full permissions: the one who defines roles. */
    fullAdmin: z.boolean(),
    organizations: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
export const Circle = z.object({
    id: z.uuid(),
    name: z.string(),
    branchId: z.uuid(),
    organizationId: z.uuid(),
});
export const CirclesResponse = z.object({ circles: z.array(Circle) });
// ---------- user management ----------
export const CreateUserRequest = z
    .object({
    name: z.string().trim().min(2).max(120),
    /** The account signs in with this number; it chooses a password at its first sign-in by code. */
    phone,
    gender: Gender.optional(),
    organizationId: z.uuid(),
    role: Role.exclude(["platform_admin"]),
    scopeType: z.enum(["organization", "branch", "circle", "self"]),
    scopeId: z.uuid().nullable(),
    /** Staff only: the role profile the new account works under (ADR 0018). */
    profileId: z.uuid().optional(),
    /** Students only: enroll the new account in this circle in the same step. */
    enrollInCircleId: z.uuid().optional(),
    /** Accepting a join request: link the request to the new account. */
    joinRequestId: z.uuid().optional(),
    /**
     * Students enrolled in a circle: the program they study (the same choices as signing up in the app,
     * owner decision 2026-11-03), approved as their plan from `planFrom` (default: today).
     */
    program: RegistrationProgram.optional(),
    planFrom: z.iso.date().optional(),
})
    .refine((v) => !v.program || (v.role === "student" && !!v.enrollInCircleId), {
    message: "الخطة للطالب المسجّل في حلقة",
    path: ["program"],
})
    .refine((v) => !v.enrollInCircleId || v.role === "student", {
    message: "التسجيل في حلقة للطلاب فقط",
    path: ["enrollInCircleId"],
});
export const CreateUserResponse = z.object({
    userId: z.uuid(),
    assignmentId: z.uuid(),
    /** The phone already had an account: the role was added to it (owner decision 2026-11-01). */
    existing: z.boolean().optional(),
    /** Staff accounts: the WhatsApp welcome with the portal and how to sign in (owner request 2026-11-01). */
    whatsapp: z.object({ sent: z.boolean(), error: z.string().nullable() }).optional(),
});
// ---------- live recitation (docs/06) ----------
export const SessionStatus = z.enum([
    "scheduled",
    "requested",
    "accepted",
    "connecting",
    "active",
    "reconnecting",
    "interrupted",
    "ended",
    "cancelled",
    "expired",
    "missed",
    "failed",
]);
export const RecitationKind = z.enum(["new", "near_review", "far_review", "repeat"]);
export const Decision = z.enum(["passed", "repeat", "partial"]);
const ayahId = z.number().int().min(1).max(6236);
const range = z
    .object({ startAyah: ayahId, endAyah: ayahId })
    .refine((r) => r.startAyah <= r.endAyah, { message: "بداية المقرر بعد نهايته", path: ["endAyah"] });
export const RequestSessionBody = z
    .object({
    enrollmentId: z.uuid().optional(),
    kind: RecitationKind.optional(),
    startAyah: ayahId.optional(),
    endAyah: ayahId.optional(),
})
    .refine((v) => (v.startAyah == null) === (v.endAyah == null), { message: "حدد بداية المقرر ونهايته معًا" })
    .refine((v) => v.startAyah == null || v.endAyah == null || v.startAyah <= v.endAyah, {
    message: "بداية المقرر بعد نهايته",
});
/** A teacher books a recitation appointment for a student of their circle (A3-c). */
export const ScheduleSessionBody = z
    .object({
    enrollmentId: z.uuid(),
    scheduledFor: z.iso.datetime({ offset: true }),
    kind: RecitationKind.optional(),
    startAyah: ayahId.optional(),
    endAyah: ayahId.optional(),
})
    .refine((v) => (v.startAyah == null) === (v.endAyah == null), { message: "حدد بداية المقرر ونهايته معًا" })
    .refine((v) => v.startAyah == null || v.endAyah == null || v.startAyah <= v.endAyah, {
    message: "بداية المقرر بعد نهايته",
});
export const SetRangeBody = range;
export const LiveSession = z.object({
    id: z.uuid(),
    status: SessionStatus,
    origin: z.enum(["scheduled", "on_demand"]),
    kind: RecitationKind.nullable(),
    startAyah: z.number().nullable(),
    endAyah: z.number().nullable(),
    /** The caller's side of the session. */
    myRole: z.enum(["student", "teacher", "observer"]),
    student: z.object({ id: z.uuid(), name: z.string() }),
    teacher: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    circle: z.object({ id: z.uuid(), name: z.string() }),
    /** For appointments booked by a teacher (A3-c); the student may join from shortly before it. */
    scheduledFor: z.string().nullable(),
    requestedAt: z.string().nullable(),
    acceptedAt: z.string().nullable(),
    activeAt: z.string().nullable(),
    endedAt: z.string().nullable(),
    endReason: z.string().nullable(),
    deadlineAt: z.string().nullable(),
    /** 1-based position among waiting requests in the circle (requested sessions only). */
    queuePosition: z.number().nullable(),
    resultId: z.uuid().nullable(),
});
export const SessionResponse = z.object({ session: LiveSession });
export const CurrentSessionResponse = z.object({ session: LiveSession.nullable() });
export const QueueResponse = z.object({ waiting: z.array(LiveSession), current: LiveSession.nullable() });
/** Upcoming appointments of the caller in the active role (student: their own; teacher: the ones they hold). */
export const AppointmentsResponse = z.object({ appointments: z.array(LiveSession) });
export const IceServersResponse = z.object({
    iceServers: z.array(z.object({
        urls: z.union([z.string(), z.array(z.string())]),
        username: z.string().optional(),
        credential: z.string().optional(),
    })),
    ttlSeconds: z.number(),
});
export const ErrorType = z.object({ id: z.uuid(), code: z.string(), name: z.string() });
export const ErrorTypesResponse = z.object({ errorTypes: z.array(ErrorType) });
export const RecordResultBody = z.object({
    decision: Decision,
    kind: RecitationKind.optional(),
    range: range.optional(),
    /** Today's plan item this recitation covers (Phase 4.3); its status follows the decision. */
    planItemId: z.uuid().optional(),
    /**
     * With decision «partial» on a plan item (B8-a): the last ayah the student recited, reading the item from its
     * start in the plan's direction. The rest is due first on the next study day.
     */
    partialUntil: ayahId.optional(),
    studentNote: z.string().trim().max(1000).optional(),
    teacherNote: z.string().trim().max(2000).optional(),
    errors: z
        .array(z.object({
        errorTypeId: z.uuid(),
        ayahId: ayahId.optional(),
        wordIndex: z.number().int().min(0).max(500).optional(),
        count: z.number().int().min(1).max(999).default(1),
        note: z.string().trim().max(500).optional(),
    }))
        .max(200)
        .default([]),
});
/** A correction (B13) restates the whole result as it should be, with the reason for the change. */
export const CorrectResultBody = RecordResultBody.omit({ planItemId: true, partialUntil: true }).extend({
    reason: z.string().trim().min(3, "اذكر سبب التعديل").max(500),
});
export const ResultRevision = z.object({
    revision: z.number(),
    decision: Decision,
    kind: RecitationKind.nullable(),
    startAyah: z.number().nullable(),
    endAyah: z.number().nullable(),
    studentNote: z.string().nullable(),
    teacherNote: z.string().nullable(),
    errors: z.array(z.object({ name: z.string(), ayahId: z.number().nullable(), count: z.number() })),
    /** Why this version was replaced, by whom and when. */
    reason: z.string(),
    changedBy: z.object({ id: z.uuid(), name: z.string() }),
    changedRole: z.string(),
    changedAt: z.string(),
});
export const ResultRevisionsResponse = z.object({ revisions: z.array(ResultRevision) });
export const RecitationResult = z.object({
    id: z.uuid(),
    sessionId: z.uuid().nullable(),
    decision: Decision,
    kind: RecitationKind.nullable(),
    startAyah: z.number().nullable(),
    endAyah: z.number().nullable(),
    studentNote: z.string().nullable(),
    /** Present only for staff. */
    teacherNote: z.string().nullable().optional(),
    teacher: z.object({ id: z.uuid(), name: z.string() }),
    student: z.object({ id: z.uuid(), name: z.string() }),
    recordedAt: z.string(),
    /** 1 for the result as recorded; each correction (B13) adds one. */
    revision: z.number(),
    correctedAt: z.string().nullable(),
    errors: z.array(z.object({
        errorTypeId: z.uuid(),
        name: z.string(),
        ayahId: z.number().nullable(),
        wordIndex: z.number().nullable(),
        count: z.number(),
        note: z.string().nullable(),
    })),
});
export const ResultResponse = z.object({ result: RecitationResult });
export const ResultsResponse = z.object({ results: z.array(RecitationResult) });
/**
 * A mistake noted in one of the student's recitations, where it was (an ayah, and the word when the teacher
 * marked one: MushafGlyph.word), for the student's mushaf (owner request 2026-10-26).
 */
export const Mistake = z.object({
    ayahId: z.number().int(),
    wordIndex: z.number().int().nullable(),
    errorType: z.object({ id: z.uuid(), code: z.string(), name: z.string() }),
    count: z.number().int(),
    note: z.string().nullable(),
    teacherName: z.string(),
    recordedAt: z.string(),
});
export const MistakesResponse = z.object({ mistakes: z.array(Mistake) });
/** Takes a place off the student's own mushaf (owner decision 2026-10-26); the results keep the mistakes. */
export const HideMistakeBody = z.object({
    ayahId: ayahId,
    wordIndex: z.number().int().min(0).max(500).nullable(),
});
/** WebSocket messages on /v1/live/ws. Signaling data is opaque to the server and relayed only between the two parties. */
const SignalMessage = z.object({
    type: z.literal("signal"),
    sessionId: z.uuid(),
    data: z.object({
        description: z
            .object({
            type: z.enum(["offer", "answer", "rollback", "pranswer"]),
            sdp: z.string().max(20_000).optional(),
        })
            .optional(),
        candidate: z
            .object({
            candidate: z.string().max(1000),
            sdpMid: z.string().nullable().optional(),
            sdpMLineIndex: z.number().nullable().optional(),
        })
            .nullable()
            .optional(),
        restart: z.boolean().optional(),
    }),
});
const MediaMessage = z.object({
    type: z.literal("media"),
    sessionId: z.uuid(),
    ok: z.boolean(),
    stats: z
        .object({
        rttMs: z.number().nullable(),
        loss: z.number().nullable(),
        jitterMs: z.number().nullable(),
        candidateType: z.string().nullable(),
    })
        .partial()
        .optional(),
});
const PingMessage = z.object({ type: z.literal("ping") });
export const ClientMessage = z.discriminatedUnion("type", [
    z.object({ type: z.literal("auth"), token: z.string().min(10).max(512), role: Role.optional() }),
    SignalMessage,
    MediaMessage,
    PingMessage,
]);
/**
 * Messages on the interview socket (INTERVIEW_WS_PATH): the same call messages, with the interview's id as
 * the session, after an auth by the applicant's room link or by a staff member's session for one interview.
 */
export const InterviewClientMessage = z.union([
    z.object({ type: z.literal("auth"), applicantToken: z.string().min(20).max(100) }),
    /** A staff member: by a session token (the app), or by the portal's session cookie when none is sent. */
    z.object({
        type: z.literal("auth"),
        token: z.string().min(10).max(512).optional(),
        role: Role.optional(),
        interviewId: z.uuid(),
    }),
    SignalMessage,
    MediaMessage,
    PingMessage,
]);
export const INTERVIEW_WS_PATH = "/v1/interviews/ws";
export const LIVE_WS_PATH = "/v1/live/ws";
export * from "./fields.js";
export * from "./admin.js";
export * from "./updates.js";
export * from "./policies.js";
export * from "./plans.js";
export * from "./attendance.js";
export * from "./record.js";
export * from "./struggling.js";
export * from "./reports.js";
export * from "./exams.js";
export * from "./privacy.js";
export * from "./notifications.js";
export * from "./inbox.js";
export * from "./recitation-log.js";
export * from "./catalogs.js";
export * from "./roles.js";
export * from "./interviews.js";
export * from "./volunteers.js";
export * from "./whatsapp.js";
export * from "./app-releases.js";
export * from "./page-titles.js";
export * from "./android-testers.js";
export * from "./registrations.js";
export * from "./availability.js";
//# sourceMappingURL=index.js.map